import {Spec} from 'immutability-helper';

import {Permissions} from 'auth/PermissionConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import ProxyFile from 'folder-items/ProxyFile';
import {EntryProtocol} from 'notebook/ELNConstants';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import ReviewConstants, {Review} from 'notebook/reviews/ReviewConstants';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import {StageEntryDocument} from 'workflows/WorkflowConstants';

import * as StageEntryActions from './StageEntryActions';
import StageEntryStore from './StageEntryStore';

export default class StageEntryFile extends ProxyFile<StageEntryDocument, typeof StageEntryStore> {
  override writable(): boolean {
    return this.proxied().permissions.writable;
  }

  override allowEdits(): boolean {
    return super.allowEdits() && !this._isLockedByReview() && !this.isDirty();
  }

  override permissions(): Permissions {
    return this.proxied().permissions;
  }

  override setPermissions(permissions: Permissions): void {
    StageEntryActions.setPermissions(this.id, permissions);
  }

  entryProtocols(): Array<EntryProtocol> {
    return this.proxied().entryVersion.entryProtocols;
  }

  override getEntryNote(): EntryNote {
    return StageEntryStore.getNote(this.id);
  }

  getStore(): typeof StageEntryStore {
    return StageEntryStore;
  }

  getState(): ItemEditorState<StageEntryDocument, NotePart, 'stageEntryDocument', 'stageEntryId'> {
    return this.getStore().getEditorState(this.id);
  }

  override setBanner(): void {
    if (this._isLockedByReview()) {
      this.setBannerText(ReviewUtil.getEditWarning(this.review()!.status));
    } else if (!this.isLatest() || this.isDirty()) {
      this.setBannerText('Editing disabled on previous versions.');
    } else if (!this.writable()) {
      this.setBannerText('Editing disabled on read-only workflow entry.');
    } else {
      this.setBannerText(null);
    }
  }

  review(): Review | undefined {
    return this.proxied().review;
  }

  _isLockedByReview(): boolean {
    return this.review() ? ReviewConstants.LOCKED_STATUSES.includes(this.review()!.status) : false;
  }

  addProtocol(protocol: EntryProtocol): void {
    StageEntryActions.addProtocol(this.id, this.proxied(), protocol);
  }

  updateProtocol(protocolCid: string, op: Spec<EntryProtocol>): void {
    StageEntryActions.updateProtocol(this.id, this.proxied(), protocolCid, op);
  }

  removeProtocol(protocolCid: string): void {
    StageEntryActions.removeProtocol(this.id, this.proxied(), protocolCid);
  }

  revertProtocol(protocol: EntryProtocol, selectionData: SelectionRestoreData): void {
    StageEntryActions.revertProtocol(this.id, this.proxied(), protocol, selectionData);
  }

  reorderProtocols(cids: Array<string>): void {
    StageEntryActions.reorderProtocols(this.id, this.proxied(), cids);
  }

  load({deferred}: {versionId?: number | null; deferred: JQueryDeferred<void>}): void {
    StageEntryActions.fetchEntryDetails(this.id, {deferred});
  }

  override cleanUp(): void {
    super.cleanUp();
    StageEntryActions.close(this.id);
  }

  override setTagSchema(tagSchema: TagSchema): void {
    StageEntryActions.setTagSchema(this.id, tagSchema);
  }

  override setTags(tags: Array<SchemaTag>): void {
    StageEntryActions.setTags(this.id, tags);
  }

  override setAuthors(authors: Array<User>): void {
    StageEntryActions.setAuthors(this.id, authors);
  }

  override async triggerUpdateNameAction(name: string): Promise<string> {
    if (name !== this.name()) {
      await StageEntryActions.saveFileData(this.id, name);
    }
    return this.name();
  }
}

StageEntryFile.prototype.proxiedField = 'stageEntryDocument';
